/**
 * Sticker and GIF platform services for the web/desktop shell (one set per app container): the StickersApi, the
 * client-direct KLIPY transport and the sticker image processor. Mock mode swaps in the in-browser store and a fake
 * KLIPY whose media the media pipeline serves locally (both code-split, loaded on first use).
 */
import type { GifTransport, MediaDeps, StickersApi } from "@bundle/core";
import type { Container } from "../container.ts";
import { initialLocale } from "../preferences.tsx";
import { createStickersApi } from "./api.ts";
import { createStickerImages } from "./image.ts";
import { browserGifTransport } from "./klipy.ts";

const byContainer = new WeakMap<Container, MediaDeps>();

/** An object whose methods wait for `target` (the mock implementations load lazily). */
function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    // Not a thenable: `then` stays undefined so the proxy is never mistaken for a promise.
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

export function stickersPlatform(container: Container): MediaDeps {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const images = createStickerImages(container.media);
  const locale = () => initialLocale(container.storage);
  let deps: MediaDeps;
  if (!container.mock) {
    deps = { api: createStickersApi(container.session), gifs: browserGifTransport, images, locale };
  } else {
    const mock = Promise.all([import("../mock/stickersApi.ts"), import("../mock/klipy.ts")]).then(([{ createMockStickersApi }, { createMockKlipy, mockGifMedia }]) => {
      container.media.mockRewrite = mockGifMedia;
      container.media.refresh();
      return { api: createMockStickersApi(() => container.session.state.session?.token), gifs: createMockKlipy() };
    });
    deps = { api: deferred<StickersApi>(mock.then((m) => m.api)), gifs: deferred<GifTransport>(mock.then((m) => m.gifs)), images, locale };
  }
  byContainer.set(container, deps);
  return deps;
}
